import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { faqs } from '@/mocks/faqs';

export default function About() {
  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '云开体育',
    url: 'https://ahtk365.cn',
    description: '云开体育是专注体育运动领域的综合服务平台，提供体育培训、赛事组织、运动装备及场馆管理等一站式解决方案。',
    foundingDate: '2018',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '400-888-6666',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: '上海',
      addressRegion: '浦东新区',
      streetAddress: '张江高科技园区体育中心A座',
    },
    sameAs: [
      'https://weibo.com/yunkai-sports',
      'https://www.douyin.com/user/yunkai-sports',
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <Navbar />

      <section className="relative w-full h-[280px] md:h-[340px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Modern%20sports%20company%20headquarters%20building%20exterior%20with%20warm%20natural%20lighting%2C%20professional%20corporate%20atmosphere%2C%20clean%20architecture%2C%20warm%20orange%20and%20cream%20tones%2C%20sporty%20brand%20identity&width=1800&height=700&seq=yunkai-about-hero&orientation=landscape"
          alt="关于云开体育"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/55"></div>
        <div className="relative z-10 text-center px-6">
          <h1 className="text-2xl md:text-4xl font-bold font-heading text-background-50 mb-3">关于我们</h1>
          <p className="text-sm md:text-base text-background-50/75">了解云开体育的故事与愿景</p>
        </div>
      </section>

      <Breadcrumb items={breadcrumbItems} />

      <main>
        {/* Brand Introduction */}
        <section className="w-full py-16 md:py-20 bg-background-50">
          <div className="w-full px-6 md:px-8">
            <div className="max-w-5xl mx-auto">
              <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
                <div className="flex-1">
                  <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-3">
                    品牌介绍
                  </span>
                  <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-5">
                    关于云开体育
                  </h2>
                  <p className="text-foreground-600 leading-relaxed mb-4">
                    云开体育成立于2018年，是一家专注体育运动领域的综合服务平台。我们致力于通过专业的体育培训、赛事组织、运动装备和场馆管理服务，让更多人享受运动带来的健康与快乐。
                  </p>
                  <p className="text-foreground-600 leading-relaxed mb-4">
                    经过多年发展，云开体育已建立起覆盖华东、华南地区的服务网络，累计为超过10万名用户提供专业体育服务。我们拥有50余位持证专业教练，与100多家优质体育场馆建立了战略合作关系。
                  </p>
                  <p className="text-foreground-600 leading-relaxed">
                    我们的使命是「让运动改变生活」，愿景是成为中国最受信赖的体育运动服务平台。无论您是运动爱好者还是专业运动员，云开体育都能为您提供专业、高效的体育服务支持。
                  </p>
                </div>
                <div className="flex-1 w-full">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20sports%20office%20interior%20with%20team%20meeting%2C%20warm%20natural%20light%2C%20clean%20minimalist%20design%2C%20sports%20brand%20identity%20on%20wall%2C%20professional%20creative%20workspace%2C%20warm%20orange%20and%20cream%20tones&width=700&height=500&seq=yunkai-about-brand&orientation=landscape"
                    alt="云开体育办公环境"
                    className="w-full rounded-xl object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="w-full py-16 md:py-20 bg-foreground-50">
          <div className="w-full px-6 md:px-8">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-3">
                  核心价值
                </span>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
                  我们的承诺
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  {
                    icon: 'ri-shield-star-line',
                    title: '专业品质',
                    desc: '汇聚行业顶尖教练和专家，以专业标准打造每一款产品和服务，确保卓越品质。',
                  },
                  {
                    icon: 'ri-heart-pulse-line',
                    title: '以用户为中心',
                    desc: '深入了解每位用户的需求，提供个性化、定制化的运动解决方案，让服务更有温度。',
                  },
                  {
                    icon: 'ri-lightbulb-flash-line',
                    title: '持续创新',
                    desc: '拥抱科技变革，不断探索体育服务的新模式，用创新驱动行业发展。',
                  },
                ].map((value, index) => (
                  <div
                    key={index}
                    className="bg-background-50 rounded-xl p-6 border border-background-200/70 text-center group hover:border-accent-200 transition-all duration-300"
                  >
                    <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-xl bg-accent-100 group-hover:bg-accent-200 transition-colors">
                      <i className={`${value.icon} text-accent-600 text-xl`}></i>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-900 mb-2">{value.title}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{value.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Reputation / Honors */}
        <section id="reputation" className="w-full py-16 md:py-20 bg-background-50">
          <div className="w-full px-6 md:px-8">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold mb-3">
                  荣誉资质
                </span>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                  行业认可与专业认证
                </h2>
                <p className="text-foreground-500 text-sm max-w-xl mx-auto">
                  多项行业荣誉和专业资质认证，是对云开体育服务品质的最好证明
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                {[
                  { title: '国家体育总局', desc: '青少年体育培训', sub: '认证机构', year: '2020' },
                  { title: '中国体育科学学会', desc: '运动训练', sub: '理事单位', year: '2021' },
                  { title: '全国群众体育', desc: '先进单位', sub: '荣誉称号', year: '2022' },
                  { title: 'ISO 9001', desc: '质量管理体系', sub: '认证企业', year: '2023' },
                  { title: '体育产业', desc: '创新企业', sub: '年度大奖', year: '2024' },
                  { title: '最佳体育', desc: '服务平台', sub: '行业评选', year: '2024' },
                  { title: '中国篮球协会', desc: '青少年培训', sub: '合作单位', year: '2025' },
                  { title: '国家体育产业', desc: '示范项目', sub: '入选项目', year: '2025' },
                ].map((honor, index) => (
                  <div
                    key={index}
                    className="bg-background-100 rounded-xl p-5 border border-background-200/70 text-center hover:border-primary-200 transition-all duration-300"
                  >
                    <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-lg bg-primary-100">
                      <i className="ri-award-line text-primary-600 text-lg"></i>
                    </div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">{honor.title}</h4>
                    <p className="text-xs text-foreground-600 mb-0.5">{honor.desc}</p>
                    <p className="text-xs text-foreground-400 mb-2">{honor.sub}</p>
                    <span className="text-[10px] text-primary-500 font-medium">{honor.year}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="w-full py-16 md:py-20 bg-background-100">
          <div className="w-full px-6 md:px-8">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-3">
                  联系我们
                </span>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                  期待与您的沟通
                </h2>
                <p className="text-foreground-500 text-sm max-w-xl mx-auto">
                  无论您有任何问题或合作需求，都可以通过以下方式联系我们
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-5">
                  {[
                    {
                      icon: 'ri-phone-line',
                      title: '电话咨询',
                      info: '400-888-6666',
                      sub: '周一至周日 9:00 - 21:00',
                    },
                    {
                      icon: 'ri-mail-line',
                      title: '电子邮箱',
                      info: 'contact@yunkai-sports.com',
                      sub: '我们会在24小时内回复',
                    },
                    {
                      icon: 'ri-map-pin-line',
                      title: '公司地址',
                      info: '上海市浦东新区张江高科技园区',
                      sub: '体育中心A座',
                    },
                    {
                      icon: 'ri-wechat-line',
                      title: '官方微信',
                      info: '云开体育',
                      sub: '关注获取最新资讯',
                    },
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-4 p-4 bg-background-50 rounded-xl border border-background-200/70">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                        <i className={`${item.icon} text-primary-600 text-lg`}></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-900 mb-0.5">{item.title}</h4>
                        <p className="text-sm font-medium text-primary-600 mb-0.5">{item.info}</p>
                        <p className="text-xs text-foreground-400">{item.sub}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
                  <div className="w-full h-full min-h-[350px]">
                    <iframe
                      title="云开体育位置"
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3412.123456789!2d121.5999999!3d31.2000000!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzHCsDEyJzAwLjAiTiAxMjHCsDM2JzAwLjAiRQ!5e0!3m2!1szh-CN!2scn!4v1690000000000!5m2!1szh-CN!2scn"
                      width="100%"
                      height="100%"
                      style={{ border: 0, minHeight: '350px' }}
                      allowFullScreen={false}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    ></iframe>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full py-16 md:py-20 bg-background-50">
          <div className="w-full px-6 md:px-8">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-3">
                  常见问题
                </span>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
                  您可能关心的问题
                </h2>
              </div>

              <div className="space-y-3">
                {faqs.map((faq, index) => (
                  <details key={index} className="group bg-background-100 rounded-xl border border-background-200/70 overflow-hidden cursor-pointer">
                    <summary className="flex items-center justify-between p-5 font-medium text-foreground-900 text-sm list-none">
                      {faq.question}
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200 text-lg"></i>
                    </summary>
                    <div className="px-5 pb-5 pt-0 text-sm text-foreground-600 leading-relaxed border-t border-background-200/70">
                      <p className="pt-4">{faq.answer}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}